import { EliteLandingPageState, type EliteOnboardingStatusResponse } from '../../types/misc.types'
import {
  isEliteAutoPayActive,
  shouldShowEliteRenewalPrompt
} from '../../components/elite/dashboard/eliteMembershipRenewalWindow'
import { getPlanSelectorEliteLandingPageState } from '../new-elite-landing/eliteLandingPlanSelectorState'
import type { EliteLandingPlan } from '../new-elite-landing/planTypes'
import {
  hasEliteReferralReward,
  isLimitedTime14MonthOffer,
  mapEligiblePlanToPlanConfig,
  mapSelectedPlanDetailsToPlanConfig,
  newEliteLandingPlanStateConfig,
  resolveCtaLabel,
  resolvePlanId,
  resolveRenewalAwareSupportText,
  resolveSelectedPlanForCta,
  updateCtaWithSelectedPlanId
} from '../new-elite-landing/components/NewEliteLandingPlanSelector'
import { buildFamilyOnboardingViewProps } from '../new-elite-landing/pages/family/familyOnboardingViewProps'
import { hasEliteConsentText } from '../../components/elite/EliteConsentTextContent'

import type { ElitePlansAdvisorySelectionViewModel } from './types/advisory.types'

export type { ElitePlansAdvisorySelectionViewModel } from './types/advisory.types'

type AdvisoryPlanConfig = NonNullable<ReturnType<typeof mapEligiblePlanToPlanConfig>>

function resolveVisiblePlanConfigs(
  status: EliteOnboardingStatusResponse,
  referralReward: EliteOnboardingStatusResponse['referralReward'],
  stateConfig: (typeof newEliteLandingPlanStateConfig)[keyof typeof newEliteLandingPlanStateConfig]
): AdvisoryPlanConfig[] {
  const eligiblePlans = status.eligiblePlans ?? []
  const useSelectedDetailsForDisplay = Boolean(stateConfig?.useSelectedPlanDetailsForDisplay)
  const mappedFromSelected = useSelectedDetailsForDisplay
    ? mapSelectedPlanDetailsToPlanConfig(status.selectedPlanDetails, eligiblePlans)
    : null

  let planConfigs: AdvisoryPlanConfig[]
  if (mappedFromSelected) {
    planConfigs = [mappedFromSelected]
  } else {
    planConfigs = eligiblePlans
      .map((plan) => mapEligiblePlanToPlanConfig(plan))
      .filter((plan): plan is AdvisoryPlanConfig => plan !== null)
  }

  planConfigs = planConfigs.map((plan) => {
    if (plan.type !== 'yearly' || !hasEliteReferralReward(referralReward)) return plan
    return { ...plan, referralReward: referralReward ?? undefined }
  })

  if (!stateConfig?.showOnlyYearlyPlan) {
    return planConfigs
  }

  const yearlyPlans = planConfigs.filter((plan) => plan.type === 'yearly')
  return yearlyPlans.length > 0 ? yearlyPlans : planConfigs
}

export function buildElitePlansAdvisorySelectionViewModel(
  status: EliteOnboardingStatusResponse,
  selectedTenure: EliteLandingPlan
): ElitePlansAdvisorySelectionViewModel {
  const showRenewalPrompt = shouldShowEliteRenewalPrompt(status)
  const isAutoPayActive = isEliteAutoPayActive(status)
  const isEliteRenewalFlow =
    showRenewalPrompt || (!isAutoPayActive && status.eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED)

  const planSelectorLandingState = getPlanSelectorEliteLandingPageState(status.eliteLandingPageState)
  const pageStateConfig = planSelectorLandingState
    ? (newEliteLandingPlanStateConfig[planSelectorLandingState] ?? {})
    : {}
  const renewOverlay = newEliteLandingPlanStateConfig[EliteLandingPageState.PLAN_EXPIRED]
  const stateConfig = isEliteRenewalFlow && renewOverlay ? { ...pageStateConfig, ...renewOverlay } : pageStateConfig

  const referralReward = status.referralReward
  const visiblePlanConfigs = resolveVisiblePlanConfigs(status, referralReward, stateConfig)
  const displayedPlanType =
    visiblePlanConfigs.find((plan) => plan.type === selectedTenure)?.type ??
    visiblePlanConfigs[0]?.type ??
    selectedTenure
  const displayedPlan = visiblePlanConfigs.find((plan) => plan.type === displayedPlanType) ?? visiblePlanConfigs[0]

  const consentFromStatus = status.consentTextList ?? []
  const showConsent =
    hasEliteConsentText(consentFromStatus) && status.eliteLandingPageState === EliteLandingPageState.ONB_NOT_INITIATED

  const liveCta = isEliteRenewalFlow ? status.renewal?.cta : status.cta
  const isCtaDisabledByState =
    Boolean(stateConfig?.isCtaDisabled) || (isEliteRenewalFlow && !(liveCta as { url?: string } | undefined)?.url)

  const suppressPlanChrome = Boolean(stateConfig?.isCtaDisabled || stateConfig?.suppressPlanSelectionChrome)
  const singleVisiblePlan = visiblePlanConfigs.length === 1
  const eligiblePlanForBanner = resolveSelectedPlanForCta(status.eligiblePlans ?? [], displayedPlanType)
  const renewExtraMonthsBannerLabel =
    singleVisiblePlan &&
    stateConfig?.singlePlanBannerLabel &&
    eligiblePlanForBanner &&
    isLimitedTime14MonthOffer(eligiblePlanForBanner)
      ? stateConfig.singlePlanBannerLabel
      : undefined

  const familyConfig = buildFamilyOnboardingViewProps(status)

  return {
    eligiblePlans: status.eligiblePlans ?? [],
    visiblePlanConfigs,
    displayedPlanType,
    displayedPlan,
    selectedTenure,
    ctaLabel: resolveCtaLabel({
      stateConfig,
      displayedPlanType,
      displayedPlan,
      familyConfig
    }),
    liveCta,
    isCtaDisabledByState,
    showConsent,
    consentTextList: consentFromStatus,
    supportText: resolveRenewalAwareSupportText(stateConfig?.supportText, status.renewal?.status),
    suppressPlanChrome,
    showPlanSection: visiblePlanConfigs.length > 0,
    planSectionTitle: stateConfig?.planSectionTitle,
    singleVisiblePlan,
    renewExtraMonthsBannerLabel,
    paymentSuccessTopBanner: stateConfig?.paymentSuccessTopBanner,
    pendingPaymentTopLabelClock: stateConfig?.pendingPaymentTopLabelClock,
    topLabel: stateConfig?.topLabel,
    referralReward,
    landingPageState: status.eliteLandingPageState,
    isEliteRenewalFlow,
    familyConfig
  }
}

export function buildElitePlansAdvisoryCtaPayload(
  status: EliteOnboardingStatusResponse | undefined,
  liveCta: EliteOnboardingStatusResponse['cta'] | undefined,
  selectedTenure: EliteLandingPlan
): Record<string, unknown> | null {
  if (!liveCta) return null

  const eligiblePlanForCta = resolveSelectedPlanForCta(status?.eligiblePlans ?? [], selectedTenure)
  const selectedPlanId = resolvePlanId(eligiblePlanForCta)
  if (!selectedPlanId) {
    return { ...(liveCta ?? {}) }
  }

  return updateCtaWithSelectedPlanId(liveCta, selectedPlanId)
}
